import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Trash2, Clock, Calendar, User, DollarSign, Info, ChevronDown, RotateCcw, X, Play, Users, UserCheck, UserX, Wallet, Search, Landmark, Receipt, Gift, ChevronRight, Eye, Star, Scale, Baby, CalendarDays, CalendarCheck, Briefcase } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

// Normalise un code filiale (ex. 'sn' -> 'SN') : évite les écarts de casse / d'espaces
const normaliserCode = (code) => (code ?? '').toString().trim().toUpperCase();

// ═══════════════════════════════════════════════════════════════════════
//  PRÉSENTATION — helpers et composants purement visuels
//  Aucune logique métier ni appel API ici : tout arrive par les props.
// ═══════════════════════════════════════════════════════════════════════

const formatMontant = (v) => {
  const num = toNumber(v);
  return num !== 0 ? num.toLocaleString('fr-FR') : '0';
};
const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const initiales = (nom, prenom) =>
  `${(nom || '').trim().charAt(0)}${(prenom || '').trim().charAt(0)}`.toUpperCase() || '?';

// Primes (mêmes champs que le cumul d'origine)
const PRIMES = [
  { key: 'prime_variable', label: 'Variable' },
  { key: 'prime_anciennete', label: 'Ancienneté' },
  { key: 'prime_caisse', label: 'Caisse' },
  { key: 'prime_responsabilite', label: 'Responsabilité' },
  { key: 'prime_astreinte', label: 'Astreinte' },
  { key: 'prime_de_deplacement', label: 'Déplacement' },
  { key: 'prime_autre', label: 'Autre' },
  { key: 'prime_gratification', label: 'Gratification' },
];
const cumulPrimes = (d) => PRIMES.reduce((s, { key }) => s + toNumber(d[key]), 0);

// Charges affichées dans la modale récap, indexées par CODE de filiale.
// Ajouter une filiale = ajouter une entrée ici (aucun JSX à dupliquer).
const CHARGES_PAR_FILIALE = {
  BJ: {
    patronales: [
      { label: 'CNSS Patronale', key: 'cnss_patronale' },
      { label: 'VPS', key: 'vps' },
    ],
    salariales: [
      { label: 'CNSS', key: 'cnss' },
      { label: 'IPTS BRUT', key: 'ipts_brut' },
      { label: 'Abattement', key: 'abattement' },
      { label: 'IPTS NET', key: 'iptsnet' },
      { label: 'Redevance ORTB', key: 'redevance_ortb' },
      { label: 'Caisse Sociale', key: 'caisse_sociale' },
    ],
  },
  CI: {
    patronales: [
      { label: 'CNPS Patronale', key: 'cnps_patronale' },
      { label: 'CMU', key: 'cmu_patronal' },
      { label: 'Accident de Travail', key: 'accident_travail' },
      { label: 'Prestations Familiales', key: 'prestation_familiale' },
      { label: "FDFP TAXE D'APPRENTISSAGE", key: 'fdfp' },
      { label: 'FDFP TAXE FORMATION PROFESSIONNELLE CONTINUE', key: 'fdfp_continu' },
    ],
    salariales: [
      { label: 'CNPS', key: 'cnps' },
      { label: 'CMU', key: 'cmu_employe' },
      { label: 'Impôt brut avant RITS', key: 'Impot_brut_avant_RICF' },
      { label: 'Charges de Famille (RITS)', key: 'Charges_Famille_RICF' },
      { label: 'TOT. RITS', key: 'Total_Impot' },
      { label: 'CAISSE SOCIALE', key: 'caisse_sociale' },
    ],
  },
  CI_HOOPE: {
    patronales: [
      { label: 'CNPS Patronale', key: 'cnps_patronale' },
      { label: 'CMU', key: 'cmu_patronal' },
      { label: 'Accident de Travail', key: 'accident_travail' },
      { label: 'Prestations Familiales', key: 'prestation_familiale' },
      { label: "FDFP TAXE D'APPRENTISSAGE", key: 'fdfp' },
      { label: 'FDFP TAXE FORMATION PROFESSIONNELLE CONTINUE', key: 'fdfp_continu' },
    ],
    salariales: [
      { label: 'CNPS', key: 'cnps' },
      { label: 'CMU', key: 'cmu_employe' },
      { label: 'Impôt brut avant RITS', key: 'Impot_brut_avant_RICF' },
      { label: 'Charges de Famille (RITS)', key: 'Charges_Famille_RICF' },
      { label: 'TOT. RITS', key: 'Total_Impot' },
      { label: 'CAISSE SOCIALE', key: 'caisse_sociale' },
    ],
  },
  CM: {
    patronales: [
      { label: 'Taxe Communale', key: 'taxe_communale' },
      { label: 'Redevence AV', key: 'redevence_AV' },
      { label: 'CNPS Pension Vieillesse', key: 'cnps_patronale' },
      { label: 'IRPP + CAC', key: 'irpp_cac', composite: true },
      { label: 'Crédit Foncier PATRONAL', key: 'credit_foncier_patron' },
      { label: 'FNE', key: 'FNE' },
      { label: 'Allocation Familiale', key: 'allocation_familiale' },
    ],
    salariales: [
      { label: 'CNPS Salariale', key: 'cnss' },
      { label: 'Accident de Travail', key: 'accident_travail' },
      { label: 'Caisse Sociale', key: 'caisse_sociale' },
    ],
  },
  GN: {
    patronales: [
      { label: 'CNSS Patronale', key: 'cnss_patronale' },
      { label: 'VRSMT Forfaitaire', key: 'versement_forfaitaire' },
      { label: "Taux d'Apprentissage", key: 'taxe_apprentissage' },
    ],
    salariales: [
      { label: 'CNSS', key: 'cnss' },
      { label: 'IND. EXON (25%)', key: 'ind_exonerees' },
      { label: 'BASE RTS', key: 'base_rts' },
      { label: 'Retenue sur Trt. de Sal', key: 'rts' },
      { label: 'CAISSE SOCIALE', key: 'caisse_sociale' },
    ],
  },
  GNB: {
    patronales: [
      { label: 'CNSS Patronale', key: 'cnss_patronale' },
      { label: 'VPS', key: 'vps' },
    ],
    salariales: [
      { label: 'CNSS', key: 'cnss' },
      { label: 'Impôt Professionnel', key: 'impot_professionnel' },
      { label: 'Droit de Timbre', key: 'abattement' },
      { label: 'Impôt Démocratique', key: 'impot_democratique' },
      { label: 'CAISSE SOCIALE', key: 'caisse_sociale' },
    ],
  },
  CG: {
    patronales: [
      { label: 'CNSS Patronale', key: 'cnss_patronale' },
      { label: 'VPS', key: 'vps' },
    ],
    salariales: [
      { label: 'CNSS', key: 'cnss' },
      { label: 'ITS Brut', key: 'ipts_brut' },
      { label: 'Abattement', key: 'abattement' },
      { label: 'ITS Net', key: 'iptsnet' },
      { label: 'Redevance ORTB', key: 'redevance_ortb' },
      { label: 'Retenue Sociale', key: 'retenue_sociale' },
    ],
  },
};

const TONES = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
  orange: 'bg-orange-100 text-orange-800',
  purple: 'bg-purple-100 text-purple-800',
  blue: 'bg-blue-100 text-blue-800',
  pink: 'bg-pink-100 text-pink-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  slate: 'bg-slate-100 text-slate-700',
};

// Onglets de la liste : chaque onglet = un jeu de colonnes (mêmes champs que l'existant pour « Présences »)
const getColonnesListe = (isInterim) => ({
  presences: [
    { label: isInterim ? 'Prés. (j)' : 'Prés. (h)', tone: 'green', get: (p) => p.heure_presence },
    { label: isInterim ? 'Fériée (j)' : 'Fériée (h)', tone: 'yellow', get: (p) => p.heure_feriee },
    { label: isInterim ? 'Abs. NJ (j)' : 'Abs. NJ (h)', tone: 'red', get: (p) => p.heure_absence_non_justifiee },
    { label: isInterim ? 'Abs. / Sanctions (j)' : 'Abs. / Sanctions (h)', tone: 'orange', get: (p) => p.heure_absence_non_justifiee_without_sanction },
    { label: isInterim ? 'Perm. / Mal. (j)' : 'Perm. / Mal. (h)', tone: 'purple', get: (p) => p.heure_absence_maladie },
    { label: isInterim ? 'Congés (j)' : 'Congés (h)', tone: 'blue', get: (p) => isInterim ? p.nombre_jour_pris_conge_annuel : p.nombre_jour_pris_conge_annuel * 8 },
    { label: isInterim ? 'Congés Mat. / Spéc (j)' : 'Congés Mat. / Spéc (h)', tone: 'pink', get: (p) => isInterim ? (p.heure_pris_conge_speciaux) : (p.heure_pris_conge_speciaux * 8) + (p.heure_conge_matrenite * 8) },
  ],
  primes: [
    { label: 'Variable', tone: 'indigo', money: true, get: (p) => p.prime_variable },
    { label: 'Ancienneté', tone: 'indigo', money: true, get: (p) => p.prime_anciennete },
    { label: 'Caisse', tone: 'indigo', money: true, get: (p) => p.prime_caisse },
    { label: 'Responsabilité', tone: 'indigo', money: true, get: (p) => p.prime_responsabilite },
    { label: 'Astreinte', tone: 'indigo', money: true, get: (p) => p.prime_astreinte },
    { label: 'Déplacement', tone: 'indigo', money: true, get: (p) => p.prime_de_deplacement },
    { label: 'Autre', tone: 'indigo', money: true, get: (p) => p.prime_autre },
    { label: 'Gratification', tone: 'indigo', money: true, get: (p) => p.prime_gratification },
    { label: 'Cumul', tone: 'slate', money: true, strong: true, get: (p) => cumulPrimes(p) },
  ],
  remuneration: [
    { label: 'Salaire de base', tone: 'slate', money: true, get: (p) => p.salaire_base_courant },
    { label: 'Salaire fixe', tone: 'slate', money: true, get: (p) => p.salaire_fixe },
    { label: 'Salaire brut', tone: 'blue', money: true, get: (p) => p.salaire_brut },
    { label: 'Avance', tone: 'orange', money: true, get: (p) => p.montant_avance },
    { label: 'Retenue', tone: 'orange', money: true, get: (p) => p.montant_retenu },
    { label: 'Caisse sociale', tone: 'purple', money: true, get: (p) => p.caisse_sociale },
    { label: 'Net à payer', tone: 'green', money: true, net: true, get: (p) => p.salaire_net },
  ],
});

const BTN =
  'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium shadow-sm transition-all duration-200 ' +
  'hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sm';

// Animations (réduites automatiquement si l'utilisateur préfère moins de mouvement)
const PaieStyles = () => (
  <style>{`
    @keyframes paieFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
    @keyframes paieModalIn { from { opacity: 0; transform: translateY(18px) scale(.97); } to { opacity: 1; transform: none; } }
    @keyframes paieFade { from { opacity: 0; } to { opacity: 1; } }
    .paie-fade-up { animation: paieFadeUp .38s cubic-bezier(.2,.8,.2,1) both; }
    .paie-modal-in { animation: paieModalIn .3s cubic-bezier(.2,.8,.2,1) both; }
    .paie-fade { animation: paieFade .25s ease-out both; }
    @keyframes paieMenuIn { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
    .paie-menu-in { animation: paieMenuIn .16s cubic-bezier(.2,.8,.2,1) both; }
    @media (prefers-reduced-motion: reduce) {
      .paie-fade-up, .paie-modal-in, .paie-fade, .paie-menu-in { animation: none; }
    }
  `}</style>
);

// Nombre qui « monte » jusqu'à sa valeur
const AnimatedNumber = ({ value, duration = 900 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(target);
      return undefined;
    }
    let raf;
    let start;
    const step = (t) => {
      if (start === undefined) start = t;
      const k = Math.min(1, (t - start) / duration);
      setDisplay(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display.toLocaleString('fr-FR')}</>;
};

// Onglets avec indicateur qui glisse + navigation clavier (← →)
const SlidingTabs = ({ tabs, active, onChange, label }) => {
  const listRef = useRef(null);
  const index = Math.max(0, tabs.findIndex((t) => t.id === active));

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    onChange(tabs[next].id);
    listRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="relative grid rounded-xl bg-gray-100 p-1"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      <span
        aria-hidden="true"
        className="absolute bottom-1 left-1 top-1 rounded-lg bg-white shadow transition-transform duration-300 ease-out"
        style={{ width: `calc((100% - 0.5rem) / ${tabs.length})`, transform: `translateX(${index * 100}%)` }}
      />
      {tabs.map((t) => {
        const Icon = t.icon;
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={`relative z-10 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
              selected ? 'text-nuit' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="hidden truncate sm:inline">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// Jauge circulaire animée
const Ring = ({ value, size = 104, stroke = 10 }) => {
  const pct = Math.max(0, Math.min(100, toNumber(value)));
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <defs>
          <linearGradient id="paieRingGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="url(#paieRingGrad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - shown / 100)}
          style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-nuit tabular-nums">
        {pct}%
      </div>
    </div>
  );
};

const MetricCard = ({ label, children, accent = false, tone, action, className = '' }) => (
  <div
    className={`rounded-xl border p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
      accent ? 'border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50' : 'border-gray-200 bg-white'
    } ${className}`}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="text-[11px] font-semibold uppercase leading-tight tracking-wide text-gray-500">{label}</span>
      {action}
    </div>
    <div className={`mt-1.5 text-lg font-semibold tabular-nums ${tone === 'danger' ? 'text-red-600' : 'text-nuit'}`}>
      {children}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-nuit">
    <span className="h-4 w-1 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
    {children}
  </h4>
);

// Répartition des primes en barres proportionnelles
const PrimeBars = ({ data }) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const total = cumulPrimes(data);
  const lignes = PRIMES.map((p) => ({ ...p, montant: toNumber(data[p.key]) })).filter((p) => p.montant > 0);
  if (lignes.length === 0) {
    return <p className="rounded-xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-400">Aucune prime sur cette période</p>;
  }
  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
      {lignes.map((l) => (
        <div key={l.key}>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-gray-600">{l.label}</span>
            <span className="font-semibold tabular-nums text-nuit">{l.montant.toLocaleString('fr-FR')}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
              style={{ width: on ? `${(l.montant / total) * 100}%` : '0%', transition: 'width 800ms cubic-bezier(.2,.8,.2,1)' }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, suffix, gradient, progress, hint, delay = 0 }) => (
  <div
    className="paie-fade-up relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className={`absolute -right-8 -top-8 h-28 w-28 rounded-full bg-gradient-to-br opacity-10 ${gradient}`} />
    <div className="relative flex items-center gap-4">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-lg ${gradient}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
        <div className="truncate text-2xl font-bold tabular-nums text-nuit">
          {value === null ? '—' : <AnimatedNumber value={value} />}
          {suffix ? <span className="ml-1.5 text-sm font-medium text-gray-400">{suffix}</span> : null}
        </div>
        {hint ? <div className="text-xs text-gray-400">{hint}</div> : null}
      </div>
    </div>
    {typeof progress === 'number' && (
      <div className="relative mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${gradient}`}
          style={{ width: `${progress}%`, transition: 'width 900ms cubic-bezier(.2,.8,.2,1)' }}
        />
      </div>
    )}
  </div>
);

// Modale « Récapitulatif » : onglets dynamiques selon la filiale (code)
const RecapModal = ({ data, filialeCode, devise, onClose, onVoirIndicateurs, onVoirPrimes }) => {
  const config = CHARGES_PAR_FILIALE[filialeCode];
  const [tab, setTab] = useState('general');

  const tabs = [
    { id: 'general', label: 'Général', icon: User },
    { id: 'heures', label: 'Heures', icon: Clock },
    { id: 'remuneration', label: 'Rémunération', icon: Wallet },
    ...(config
      ? [
          { id: 'patronales', label: 'Charges patronales', icon: Landmark },
          { id: 'salariales', label: 'Salariales & net', icon: Receipt },
        ]
      : []),
  ];

  const montant = (key) => {
    if (key === 'irpp_cac') {
      return formatMontant((parseFloat(data.iptsnet) || 0) + (parseFloat(data.cac) || 0));
    }
    return formatMontant(data[key]);
  };
  const dateEntree = data.date_entree ? new Date(data.date_entree).toLocaleDateString('fr-FR') : '-';
  const noteAffichee =
    data.note_pondere_quantitative || data.note_pondere_qualitative
      ? `NP.Quanti: ${data.note_pondere_quantitative || 0} | NP.Quali: ${data.note_pondere_qualitative || 0}`
      : data.note_pondere || 0;
  const infoBtn = (onClick, title) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="rounded-full p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800"
    >
      <Info size={16} />
    </button>
  );

  return (
    <div className="paie-fade fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="paie-modal-in mx-4 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-custom-lg">
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-bleu px-6 pb-6 pt-5 text-white">
          <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-white/5 blur-3xl" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="absolute right-4 top-4 rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="relative flex flex-col gap-5 pr-8 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 text-xl font-bold ring-1 ring-white/30">
                {initiales(data.nom, data.prenom)}
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-medium uppercase tracking-widest text-white/70">Récapitulatif de paie</div>
                <h3 className="truncate text-xl font-semibold">Récapitulatif : {data.nom} {data.prenom}</h3>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-white/85">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1">
                    <Briefcase className="h-3 w-3" /> {data.libelle_contrat_courant || '-'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1">
                    <Calendar className="h-3 w-3" /> {dateEntree}
                  </span>
                  {filialeCode ? (
                    <span className="inline-flex items-center rounded-full bg-white/25 px-2.5 py-1 font-semibold tracking-wide">
                      {filialeCode}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="sm:text-right">
              <div className="text-xs font-medium uppercase tracking-widest text-white/70">Net à payer</div>
              <div className="mt-1 text-3xl font-bold tabular-nums sm:text-4xl">
                <AnimatedNumber value={toNumber(data.salaire_net)} />
                <span className="ml-2 text-base font-medium text-white/70">{devise}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="border-b border-gray-100 bg-white px-6 py-3">
          <SlidingTabs tabs={tabs} active={tab} onChange={setTab} label="Sections du récapitulatif" />
        </div>

        {/* Contenu */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/70 px-6 py-6">
          <div key={tab} role="tabpanel" className="paie-fade-up">
            {tab === 'general' && (
              <div className="space-y-6">
                <div>
                  <SectionTitle>Informations générales</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <MetricCard label="Nom et prénom">{data.nom} {data.prenom}</MetricCard>
                    <MetricCard label="Type de contrat">{data.libelle_contrat_courant || '-'}</MetricCard>
                    <MetricCard label="Nombre d'enfants à charge">{data.nombre_enfant_charge || 0}</MetricCard>
                    <MetricCard label="Date entrée">{dateEntree}</MetricCard>
                  </div>
                </div>
                <div>
                  <SectionTitle>Aperçu de la période</SectionTitle>
                  <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
                    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-5">
                      <Ring value={data.taux_presence} />
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Tx Présence</span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <MetricCard label="Hr à Payer">{data.heure_payer || 0}</MetricCard>
                      <MetricCard label="Salaire Brut">{montant('salaire_brut')}</MetricCard>
                      <MetricCard label="Net à payer" accent>
                        <span className="text-rouge">{montant('salaire_net')}</span>
                      </MetricCard>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === 'heures' && (
              <div>
                <SectionTitle>Informations liées aux heures</SectionTitle>
                <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
                  <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-5">
                    <Ring value={data.taux_presence} />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Tx Présence</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    <MetricCard label="Hr Présence">{data.heure_presence || 0}</MetricCard>
                    <MetricCard label="Hr planifiée">{data.heure_planifiee || 0}</MetricCard>
                    <MetricCard label="Hr à Payer">{data.heure_payer || 0}</MetricCard>
                    <MetricCard label="Hr Fériées">{data.heure_feriee || 0}</MetricCard>
                    <MetricCard label="Hr Absence non justifiées" tone="danger">{data.heure_absence_non_justifiee || 0}</MetricCard>
                    <MetricCard label="Hr Absence sans sanctions">{data.heure_absence_non_justifiee_without_sanction || 0}</MetricCard>
                    <MetricCard label="Hr Absence maladie">{data.heure_absence_maladie || 0}</MetricCard>
                    <MetricCard label="Tx Horaire">{data.taux_horaire || 0}</MetricCard>
                    <MetricCard label="Tx Ancienneté">{data.taux_primeanciennete || 0}%</MetricCard>
                    <MetricCard label="Congés Annuels">{data.nombre_jour_pris_conge_annuel || 0}</MetricCard>
                    <MetricCard label="Heures Cong. Spéciaux">{data.heure_pris_conge_speciaux || 0}</MetricCard>
                  </div>
                </div>
              </div>
            )}

            {tab === 'remuneration' && (
              <div className="space-y-6">
                <div>
                  <SectionTitle>Salaire de référence</SectionTitle>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <MetricCard label="Salaire de Base">{montant('salaire_base_courant')}</MetricCard>
                    <MetricCard label="Sursalaire">{montant('sursalaire')}</MetricCard>
                    <MetricCard label="Salaire Fixe">{montant('salaire_fixe')}</MetricCard>
                    <MetricCard label="Note Pondérée" action={infoBtn(onVoirIndicateurs, 'Voir le détail des indicateurs')}>
                      <span className="text-sm">{noteAffichee}</span>
                    </MetricCard>
                  </div>
                </div>
                <div>
                  <SectionTitle>Primes & allocations</SectionTitle>
                  <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
                    <div className="grid grid-cols-2 gap-4">
                      <MetricCard label="Cumul Prime" accent action={infoBtn(onVoirPrimes, 'Voir le détail des primes')}>
                        {cumulPrimes(data).toLocaleString('fr-FR')}
                      </MetricCard>
                      <MetricCard label="Rémunération Fériée">{montant('remuneration_jour_ferie')}</MetricCard>
                      <MetricCard label="Moy. Mens. (Cong. Ann.)">{montant('moyenne_mensuelle')}</MetricCard>
                      <MetricCard label="Alloc Cong. Ann.">{montant('allocation_conge_annuel')}</MetricCard>
                      <MetricCard label="Tx Cong. Spéc.">{data.taux_conge_speciaux || 0}</MetricCard>
                      <MetricCard label="Alloc. Cong. Spéc.">{montant('allocation_conge_speciaux')}</MetricCard>
                    </div>
                    <PrimeBars data={data} />
                  </div>
                </div>
                <div>
                  <SectionTitle>Brut & retenues</SectionTitle>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                    <MetricCard label="Salaire Brut" accent>{montant('salaire_brut')}</MetricCard>
                    <MetricCard label="SBI">{montant('salaire_brut_imposable')}</MetricCard>
                    <MetricCard label="Trop/Moins Perçu">{montant('montant_regularisation')}</MetricCard>
                    <MetricCard label="Avance">{montant('montant_avance')}</MetricCard>
                    <MetricCard label="Retenue">{montant('montant_retenu')}</MetricCard>
                  </div>
                </div>
              </div>
            )}

            {tab === 'patronales' && config && (
              <div>
                <SectionTitle>Charges patronales</SectionTitle>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {config.patronales.map(({ label, key }) => (
                    <MetricCard key={key} label={label}>
                      {montant(key)} <span className="text-xs font-medium text-gray-400">{devise}</span>
                    </MetricCard>
                  ))}
                </div>
              </div>
            )}

            {tab === 'salariales' && config && (
              <div className="space-y-6">
                <div>
                  <SectionTitle>Charges salariales</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {config.salariales.map(({ label, key }) => (
                      <MetricCard key={key} label={label}>
                        {montant(key)} <span className="text-xs font-medium text-gray-400">{devise}</span>
                      </MetricCard>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-2xl border-l-4 border-red-500 bg-white p-5 shadow-sm ring-1 ring-gray-100">
                  <span className="text-sm font-bold uppercase tracking-wide text-nuit">Net à payer</span>
                  <span className="text-3xl font-bold tabular-nums text-rouge">
                    <AnimatedNumber value={toNumber(data.salaire_net)} />
                    <span className="ml-2 text-base font-medium text-gray-400">{devise}</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pied */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-white p-4">
          <span className="hidden text-xs text-gray-400 sm:inline">Astuce : utilisez ← → pour changer d'onglet</span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-xl bg-nuit px-6 py-2 text-white transition-colors hover:bg-nuit-2"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  PANNEAU D'ACTIONS — tiroir latéral qui coulisse depuis la droite
//  Fond estompé, animation d'entrée / sortie, focus piégé, Échap, ↑ ↓,
//  défilement interne : tous les choix sont toujours accessibles.
// ═══════════════════════════════════════════════════════════════════════

const ICON_TONES = {
  purple: { base: 'bg-purple-50 text-purple-600', hover: 'group-hover:bg-purple-600 group-hover:text-white' },
  green: { base: 'bg-emerald-50 text-emerald-600', hover: 'group-hover:bg-emerald-600 group-hover:text-white' },
  blue: { base: 'bg-blue-50 text-blue-600', hover: 'group-hover:bg-blue-600 group-hover:text-white' },
  amber: { base: 'bg-amber-50 text-amber-600', hover: 'group-hover:bg-amber-500 group-hover:text-white' },
  indigo: { base: 'bg-indigo-50 text-indigo-600', hover: 'group-hover:bg-indigo-600 group-hover:text-white' },
  pink: { base: 'bg-pink-50 text-pink-600', hover: 'group-hover:bg-pink-600 group-hover:text-white' },
  teal: { base: 'bg-teal-50 text-teal-600', hover: 'group-hover:bg-teal-600 group-hover:text-white' },
  red: { base: 'bg-red-50 text-red-600', hover: 'group-hover:bg-red-600 group-hover:text-white' },
};

const ActionDrawer = ({ open, agent, sections, devise, onClose }) => {
  const panelRef = useRef(null);
  const [entered, setEntered] = useState(false);
  const visible = entered && open;

  // Entrée : on monte le panneau hors-écran puis on le fait coulisser
  useEffect(() => {
    let r2;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(r1);
      if (r2) cancelAnimationFrame(r2);
    };
  }, []);

  // Bloque le défilement de la page + rend le focus au bouton « Action » à la fermeture
  useEffect(() => {
    const precedent = document.activeElement;
    const overflowAvant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflowAvant;
      if (precedent && typeof precedent.focus === 'function' && document.contains(precedent)) {
        precedent.focus({ preventScroll: true });
      }
    };
  }, []);

  // Focus initial dans le panneau
  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(() => {
      panelRef.current?.querySelector('[role="menuitem"]:not([disabled])')?.focus({ preventScroll: true });
    }, 120);
    return () => clearTimeout(t);
  }, [visible]);

  // Clavier : Échap, ↑ ↓ Début Fin, Tab piégé dans le panneau
  useEffect(() => {
    const onKey = (e) => {
      const panel = panelRef.current;
      if (!panel) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const f = Array.from(panel.querySelectorAll('button:not([disabled])'));
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
      const list = Array.from(panel.querySelectorAll('[role="menuitem"]:not([disabled])'));
      if (!list.length) return;
      e.preventDefault();
      const i = list.indexOf(document.activeElement);
      let next = 0;
      if (e.key === 'ArrowDown') next = i < 0 ? 0 : (i + 1) % list.length;
      else if (e.key === 'ArrowUp') next = i < 0 ? list.length - 1 : (i - 1 + list.length) % list.length;
      else if (e.key === 'End') next = list.length - 1;
      list[next].focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const net = toNumber(agent.salaire_net);
  const brut = toNumber(agent.salaire_brut);
  const presence = Math.max(0, Math.min(100, toNumber(agent.taux_presence)));
  let rang = 0;

  return createPortal(
    <div className="fixed inset-0 z-[60]" role="presentation">
      {/* Fond estompé : un clic ferme le panneau */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`absolute inset-0 bg-slate-900/45 backdrop-blur-[2px] transition-opacity duration-300 ease-out motion-reduce:transition-none ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Panneau */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Actions pour ${agent.nom || ''} ${agent.prenom || ''}`}
        className={`absolute right-0 top-0 flex h-full w-full max-w-[420px] flex-col bg-gray-50 shadow-2xl transition-transform duration-[320ms] ease-[cubic-bezier(.22,.9,.3,1)] motion-reduce:transition-none ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* En-tête */}
        <div className="relative flex-shrink-0 overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 px-5 pb-16 pt-5 text-white">
          <div className="pointer-events-none absolute -right-14 -top-16 h-52 w-52 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-44 w-44 rounded-full bg-fuchsia-400/20 blur-3xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="text-[11px] font-medium uppercase tracking-widest text-white/70">Actions sur la paie</div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer le panneau"
              className="-mr-1 -mt-1 rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative mt-3 flex items-center gap-4">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 text-xl font-bold ring-1 ring-white/30">
              {initiales(agent.nom, agent.prenom)}
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-lg font-semibold leading-tight">{agent.nom} {agent.prenom}</h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-white/90">
                {agent.matricule ? (
                  <span className="rounded-full bg-white/15 px-2.5 py-0.5">Mat. {agent.matricule}</span>
                ) : null}
                {agent.libelle_contrat_courant && agent.libelle_contrat_courant !== '-' ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5">
                    <Briefcase className="h-3 w-3" /> {agent.libelle_contrat_courant}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Résumé chiffré (chevauche l'en-tête) */}
        <div className="relative z-[1] -mt-11 flex-shrink-0 px-5">
          <div className="grid grid-cols-3 divide-x divide-gray-100 rounded-2xl bg-white p-3 shadow-lg ring-1 ring-black/5">
            <div className="px-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Net à payer</div>
              <div className="mt-0.5 truncate text-base font-bold tabular-nums text-rouge">
                {net > 0 ? <AnimatedNumber value={net} /> : '—'}
              </div>
              {net > 0 ? <div className="text-[10px] text-gray-400">{devise}</div> : null}
            </div>
            <div className="px-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Brut</div>
              <div className="mt-0.5 truncate text-base font-bold tabular-nums text-nuit">
                {brut > 0 ? formatMontant(brut) : '—'}
              </div>
              {brut > 0 ? <div className="text-[10px] text-gray-400">{devise}</div> : null}
            </div>
            <div className="px-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Présence</div>
              <div className="mt-0.5 text-base font-bold tabular-nums text-nuit">{presence > 0 ? `${presence}%` : '—'}</div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
                  style={{ width: visible ? `${presence}%` : '0%', transition: 'width 800ms cubic-bezier(.2,.8,.2,1) 250ms' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div role="menu" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-4">
          {sections.map((section) => {
            const visibles = section.items.filter((it) => !it.hidden);
            if (visibles.length === 0) return null;
            const danger = section.items.some((it) => it.danger);
            return (
              <div key={section.title} className="mb-5 last:mb-0">
                <div className="mb-2 flex items-center gap-2 px-1">
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${danger ? 'text-red-400' : 'text-gray-400'}`}>
                    {section.title}
                  </span>
                  <span className="h-px flex-1 bg-gray-200" />
                </div>
                <div className="space-y-2">
                  {visibles.map((it) => {
                    const Icon = it.icon;
                    const tone = ICON_TONES[it.tone] || ICON_TONES.blue;
                    const delai = 140 + rang * 40;
                    rang += 1;
                    return (
                      <button
                        key={it.key}
                        type="button"
                        role="menuitem"
                        disabled={it.disabled}
                        title={it.disabled ? it.disabledHint : undefined}
                        onClick={it.onClick}
                        style={{ animationDelay: `${delai}ms`, animationFillMode: 'backwards' }}
                        className={`group flex w-full items-center gap-3.5 rounded-2xl border bg-white px-3.5 py-3 text-left shadow-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                          visible ? 'paie-fade-up' : 'opacity-0'
                        } ${
                          it.danger
                            ? 'border-red-100 enabled:hover:border-red-300 enabled:hover:bg-red-50'
                            : 'border-gray-100 enabled:hover:-translate-y-0.5 enabled:hover:border-blue-200 enabled:hover:shadow-md'
                        }`}
                      >
                        <span
                          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${tone.base} ${
                            it.disabled ? '' : tone.hover
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={`block text-sm font-semibold ${it.danger ? 'text-red-600' : 'text-gray-800'}`}>{it.label}</span>
                          <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                            {it.disabled && it.disabledHint ? it.disabledHint : it.desc}
                          </span>
                        </span>
                        <ChevronRight
                          className={`h-4 w-4 flex-shrink-0 text-gray-300 transition-transform duration-200 ${
                            it.disabled ? '' : 'group-hover:translate-x-1 group-hover:text-gray-500'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pied */}
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-3">
          <span className="hidden text-[11px] text-gray-400 sm:inline">↑ ↓ naviguer · Échap fermer</span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-xl bg-nuit px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-nuit-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Fermer
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
};

const LISTE_TABS = [
  { id: 'presences', label: 'Présences & absences', icon: Clock },
  { id: 'primes', label: 'Primes', icon: Gift },
  { id: 'remuneration', label: 'Rémunération', icon: Wallet },
];

const CalculPaie = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { filialeId, isConsolidated, currentFiliale } = useFiliale();
  const [personnel, setPersonnel] = useState([]);
  const [typePersonnel, setTypePersonnel] = useState(null);
  const [paieEnCours, setPaieEnCours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [nombreNonIntegre, setNombreNonIntegre] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [paieGeneree, setPaieGeneree] = useState(false);
  const [personnelIntegres, setPersonnelIntegres] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(null);
  const [modalData, setModalData] = useState({});
  const [banques, setBanques] = useState([]);
  const [infopaieData, setInfopaieData] = useState({});
  const [primesModalOpen, setPrimesModalOpen] = useState(false);
  const [indicateursModalOpen, setIndicateursModalOpen] = useState(false);
  const [indicateursData, setIndicateursData] = useState([]);
  const [listTab, setListTab] = useState('presences');
  const [isInterimMode, setIsInterimMode] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('=== CalculPaie loadData ===');
      console.log('typeId:', typeId);
      console.log('filialeId:', filialeId);
      console.log('isConsolidated:', isConsolidated);

      // En mode non consolidé, attendre que filialeId soit disponible
      if (!isConsolidated && (!filialeId || filialeId === 'all')) {
        console.log('En attente de filialeId...');
        setLoading(false);
        return;
      }
      
      // Récupérer le type de personnel
      const typesRes = await http.get('/api/types-personnel');
      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const typePersonnel = allTypes.find(t => t.idtype_personnel === parseInt(typeId));
      setTypePersonnel(typePersonnel);

      // Déterminer si c'est une paie intérim (basé sur l'activité du type de personnel)
      const isInterim = typePersonnel?.idactivite === 4;
      setIsInterimMode(isInterim);

      // Récupérer la période de paie en cours
      let endpoint = isInterim ? '/api/paies/interim/encours' : '/api/paies/encours';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `?filiale_id=${filialeId}`;
      }
      const paieRes = await http.get(endpoint).catch(() => ({ data: null }));
      setPaieEnCours(paieRes.data);

      // Vérifier si la paie a été générée pour ce type
      if (paieRes.data) {
        let genereeEndpoint = `/api/paies/est-generee/${typeId}`;
        if (!isConsolidated && filialeId && filialeId !== 'all') {
          genereeEndpoint += `?filiale_id=${filialeId}`;
        }
        const genereeRes = await http.get(genereeEndpoint).catch(() => ({ data: { generee: false } }));
        setPaieGeneree(genereeRes.data.generee);

        // Récupérer le nombre d'agents non intégrés
        let nonIntegresEndpoint = `/api/paies/non-integres/${typeId}`;
        if (!isConsolidated && filialeId && filialeId !== 'all') {
          nonIntegresEndpoint += `?filiale_id=${filialeId}`;
        }
        nonIntegresEndpoint += `${nonIntegresEndpoint.includes('?') ? '&' : '?'}isInterim=${isInterim}`;
        const nonIntegresRes = await http.get(nonIntegresEndpoint).catch(() => ({ data: { nombre: 0 } }));
        setNombreNonIntegre(nonIntegresRes.data.nombre);

        // Récupérer la liste des employés déjà intégrés
        const paieIdField = isInterim ? 'idpaie_interime' : 'idpaie';
        const paieId = paieRes.data[paieIdField];
        if (paieId) {
          const integresRes = await http.get(`/api/paie-element?paie_id=${paieId}&type_personnel_id=${typeId}&isInterim=${isInterim}`);
          const integreIds = Array.isArray(integresRes.data) ? integresRes.data.map(p => p.personnel_id) : [];
          setPersonnelIntegres(new Set(integreIds));
        }
      }

      // Récupérer le personnel du type
      const personnelRes = await http.get('/api/personnel');
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];
      
      // Filtrer par type de personnel et filiale
      let filteredPersonnel = allPersonnel.filter(p => p.type_personnel_id === parseInt(typeId) && p.active === 1);
      
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        filteredPersonnel = filteredPersonnel.filter(p => p.filiale_id === parseInt(filialeId));
      }

      // Fusionner avec les données de paie_element ou paie_element_interim pour les employés intégrés
      let mergedPersonnel = filteredPersonnel.map(p => ({
        ...p,
        heure_presence: 0,
        heure_feriee: 0,
        heure_absence_non_justifiee: 0,
        heure_absence_non_justifiee_without_sanction: 0,
        heure_absence_maladie: 0,
        nombre_jour_pris_conge_annuel: 0,
        heure_pris_conge_speciaux: 0,
        heure_conge_matrenite: 0,
        prime_anciennete: 0,
        prime_astreinte: 0,
        prime_caisse: 0,
        prime_responsabilite: 0,
        prime_de_deplacement: 0,
        prime_autre: 0,
        prime_gratification: 0,
        moyenne_mensuelle: 0,
        taux_horaire: 0
      }));

      // Récupérer les banques de la filiale
      let banquesEndpoint = '/api/banques';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        banquesEndpoint += `?filiale_id=${filialeId}`;
      }
      const banquesRes = await http.get(banquesEndpoint).catch(() => ({ data: [] }));
      setBanques(Array.isArray(banquesRes.data) ? banquesRes.data : []);

      // Récupérer les données de paie_element et fusionner
      const paieId = isInterim ? paieRes.data?.idpaie_interime : paieRes.data?.idpaie;
      if (paieId) {
        const paieElementRes = await http.get(`/api/paie-element?paie_id=${paieId}&type_personnel_id=${typeId}&isInterim=${isInterim}`);
        const paieElements = Array.isArray(paieElementRes.data) ? paieElementRes.data : [];

        mergedPersonnel = mergedPersonnel.map(p => {
          const paieData = paieElements.find(pe => pe.personnel_id === p.idpersonnel);
          if (paieData) {
            return {
              ...p,
              filiale_id: p.filiale_id,
              filiale_code: p.filiale_code,
              // Heures (mode normal)
              heure_presence: paieData.heure_presence || 0,
              heure_feriee: paieData.heure_feriee || 0,
              heure_absence_non_justifiee: paieData.heure_absence_non_justifiee || 0,
              heure_absence_non_justifiee_without_sanction: paieData.heure_absence_non_justifiee_without_sanction || 0,
              heure_absence_maladie: paieData.heure_absence_maladie || 0,
              heure_programme: paieData.heure_programme || 0,
              heure_payer: paieData.heure_payer || 0,
              heure_planifiee: paieData.heure_planifiee || 0,
              taux_presence: paieData.taux_presence || 0,
              taux_horaire: paieData.taux_horaire || 0,
              taux_primeanciennete: paieData.taux_primeanciennete || 0,
              // Jours (mode intérim)
              nbreJrs_paie_courante: paieData.nbreJrs_paie_courante || 0,
              nbreJrs_paie_passee: paieData.nbreJrs_paie_passee || 0,
              nbreJrs_paie_suivante: paieData.nbreJrs_paie_suivante || 0,
              // Congés
              nombre_jour_pris_conge_annuel: paieData.nombre_jour_pris_conge_annuel || 0,
              heure_pris_conge_speciaux: paieData.heure_pris_conge_speciaux || 0,
              heure_conge_matrenite: paieData.heure_conge_matrenite || 0,
              moyenne_mensuelle: paieData.moyenne_mensuelle || 0,
              remuneration_jour_ferie: paieData.remuneration_jour_ferie || 0,
              allocation_conge_annuel: paieData.allocation_conge_annuel || 0,
              allocation_conge_speciaux: paieData.allocation_conge_speciaux || 0,
              taux_conge_speciaux: paieData.taux_conge_speciaux || 0,
              montant_conge_matrenite: paieData.montant_conge_matrenite || 0,
              // Salaires
              salaire_base_courant: paieData.salaire_base_courant || 0,
              sursalaire: paieData.titre_sursalaire || 0,
              salaire_fixe: paieData.salaire_fixe || 0,
              salaire_brut: paieData.salaire_brut || 0,
              salaire_net: paieData.salaire_net || 0,
              // Primes
              prime_anciennete: paieData.prime_anciennete || 0,
              prime_astreinte: paieData.prime_astreinte || 0,
              prime_caisse: paieData.prime_caisse || 0,
              prime_responsabilite: paieData.prime_responsabilite || 0,
              prime_de_deplacement: paieData.prime_de_deplacement || 0,
              prime_autre: paieData.prime_autre || 0,
              prime_gratification: paieData.prime_gratification || 0,
              prime_variable: paieData.prime_variable || 0,
              prime_variable_sans_absence: paieData.prime_variable_sans_absence || 0,
              prime_de_perdiem: paieData.prime_de_perdiem || 0,
              // Notes pondérées
              note_pondere: paieData.note_pondere || 0,
              note_pondere_quantitative: paieData.note_pondere_quantitative || 0,
              note_pondere_qualitative: paieData.note_pondere_qualitative || 0,
              // Charges
              cnss: paieData.cnss || 0,
              ipts_brut: paieData.ipts_brut || 0,
              abattement: paieData.abattement || 0,
              iptsnet: paieData.iptsnet || 0,
              redevance_ortb: paieData.redevance_ortb || 0,
              cnss_patronale: paieData.cnss_patronale || 0,
              vps: paieData.vps || 0,
              caisse_sociale: paieData.caisse_sociale || 0,
              retenue_sociale: paieData.retenue_sociale || 0,
              // Charges CI
              cnps: paieData.cnps || 0,
              impot_salaire: paieData.impot_salaire || 0,
              contribution_nationale: paieData.contribution_nationale || 0,
              igr: paieData.igr || 0,
              cmu_employe: paieData.cmu_employe || 0,
              cmu_patronal: paieData.cmu_patronal || 0,
              cnps_patronale: paieData.cnps_patronale || 0,
              impot_salaire_patronale: paieData.impot_salaire_patronale || 0,
              accident_travail: paieData.accident_travail || 0,
              prestation_familiale: paieData.prestation_familiale || 0,
              // Charges CM
              taxe_communale: paieData.taxe_communale || 0,
              credit_foncier: paieData.credit_foncier || 0,
              redevence_AV: paieData.redevence_AV || 0,
              cac: paieData.cac || 0,
              credit_foncier_patron: paieData.credit_foncier_patron || 0,
              FNE: paieData.FNE || 0,
              allocation_familiale: paieData.allocation_familiale || 0,
              fdfp: paieData.fdfp || 0,
              fdfp_continu: paieData.fdfp_continu || 0,
              salaire_brut_imposable: paieData.salaire_brut_imposable || 0,
              Impot_brut_avant_RICF: paieData.Impot_brut_avant_RICF || 0,
              Charges_Famille_RICF: paieData.Charges_Famille_RICF || 0,
              Total_Impot: paieData.Total_Impot || 0,
              // Charges GN
              versement_forfaitaire: paieData.versement_forfaitaire || 0,
              taxe_apprentissage: paieData.taxe_apprentissage || 0,
              ind_exonerees: paieData.ind_exonerees || 0,
              base_rts: paieData.base_rts || 0,
              rts: paieData.rts || 0,
              // Autres
              montant_regularisation: paieData.montant_regularisation || 0,
              montant_avance: paieData.montant_avance || 0,
              montant_retenu: paieData.montant_retenu || 0,
              type_contrat_courant: paieData.type_contrat_courant || null,
              libelle_contrat_courant: paieData.contrat_nom || p.libelle_contrat || '-'
            };
          }
          return p;
        });
      }

      setPersonnel(mergedPersonnel);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, filialeId, isConsolidated, currentFiliale]);

  const filteredPersonnel = personnel.filter(p =>
    p.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.matricule?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPersonnel = filteredPersonnel.slice(startIndex, endIndex);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Ouverture / fermeture du panneau d'actions (la fermeture laisse le temps à l'animation de sortie)
  const ouvrirMenu = (id) => {
    setDropdownOpen(id);
    setMenuVisible(true);
  };
  const fermerMenu = () => setMenuVisible(false);
  const toggleDropdown = (id) => {
    if (dropdownOpen === id && menuVisible) fermerMenu();
    else ouvrirMenu(id);
  };

  useEffect(() => {
    if (menuVisible || dropdownOpen === null) return undefined;
    const t = setTimeout(() => setDropdownOpen(null), 340);
    return () => clearTimeout(t);
  }, [menuVisible, dropdownOpen]);

  const handleGenererPaie = async () => {
    if (!paieEnCours) {
      alert('Aucune période de paie ouverte');
      return;
    }

    if (!window.confirm('Voulez-vous générer la paie complète pour tous les employés intégrés ?')) {
      return;
    }

    try {
      setLoading(true);
      await http.post(`/api/paies/generer-block/${typeId}`, { isInterim: isInterimMode });
      alert('Paie générée avec succès');
      loadData();
    } catch (error) {
      console.error('Erreur génération paie:', error);
      alert('Erreur lors de la génération de la paie: ' + (error.response?.data?.error || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  // Code de la filiale de l'agent affiché dans le récap (on n'utilise jamais l'id numérique)
  const recapFilialeCode = normaliserCode(modalData?.filiale_code || currentFiliale?.code);

  // Valeurs dérivées pour l'affichage (lecture seule de l'état existant)
  const deviseAffichee = currentFiliale?.devise || 'FCFA';
  const nombreIntegres = personnel.filter((p) => personnelIntegres.has(p.idpersonnel)).length;
  const tauxIntegration = personnel.length ? Math.round((nombreIntegres / personnel.length) * 100) : 0;
  const masseNette = personnel.reduce((somme, p) => somme + toNumber(p.salaire_net), 0);
  const colonnesListe = getColonnesListe(isInterimMode)[listTab] || getColonnesListe(isInterimMode).presences;

  // Même comportement que l'ancien bouton « i » de la Note Pondérée
  const handleVoirIndicateurs = async () => {
    try {
      const paieId = paieEnCours?.idpaie;
      if (paieId && modalData.idpersonnel) {
        const res = await http.get(`/api/performance?personnel_ids=${modalData.idpersonnel}`);
        setIndicateursData(Array.isArray(res.data) ? res.data : []);
      }
      setIndicateursModalOpen(true);
    } catch (error) {
      console.error('Erreur chargement indicateurs:', error);
      alert('Erreur lors du chargement des indicateurs');
    }
  };

  // ── Actions du menu « Action » (même logique métier qu'avant, regroupée par thème) ──
  const buildActionSections = (p) => {
    const fermer = fermerMenu;
    const indispoHint = 'Disponible après « Générer la paie »';
    const ouvrir = (nom, data) => {
      setModalData(data);
      setModalOpen(nom);
      fermer();
    };
    const identite = { personnelId: p.idpersonnel, nom: p.nom, prenom: p.prenom };

    return [
      {
        title: 'Consulter',
        items: [
          {
            key: 'recap',
            label: 'Récapitulatif',
            desc: 'Voir le détail complet de la paie',
            icon: Eye,
            tone: 'purple',
            onClick: () => {
              setModalData(p);
              setModalOpen('recap');
              fermer();
            },
          },
        ],
      },
      {
        title: 'Calcul',
        items: [
          {
            key: 'recalcul',
            label: 'Recalculer',
            desc: 'Relancer le calcul pour cet agent',
            icon: RefreshCw,
            tone: 'green',
            disabled: !paieGeneree,
            disabledHint: indispoHint,
            onClick: async () => {
              if (!paieEnCours) {
                alert("Merci d'ouvrir une nouvelle période de paie");
                return;
              }
              const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;
              try {
                await http.post(`/api/paies/generer/${p.idpersonnel}/${paieId}/${typeId}`, { isInterim: isInterimMode });
                fermer();
                loadData();
                alert('Paie recalculée avec succès');
              } catch (error) {
                console.error('Erreur recalcul:', error);
                alert('Erreur lors du recalcul de la paie');
              }
            },
          },
        ],
      },
      {
        title: 'Saisies du mois',
        items: [
          {
            key: 'heures',
            label: 'Infos heures',
            desc: 'Présence et heures fériées',
            icon: Clock,
            tone: 'blue',
            disabled: !paieGeneree,
            disabledHint: indispoHint,
            onClick: () => {
              if (isInterimMode) {
                // Mode intérim : utiliser les champs de jours
                ouvrir('infosHeures', {
                  ...identite,
                  nbreJrs_paie_courante: p.nbreJrs_paie_courante || 0,
                  nbreJrs_paie_passee: p.nbreJrs_paie_passee || 0,
                  nbreJrs_paie_suivante: p.nbreJrs_paie_suivante || 0,
                });
              } else {
                // Mode normal : utiliser les champs d'heures
                ouvrir('infosHeures', {
                  ...identite,
                  heure_presence: p.heure_presence || 0,
                  heure_feriee: p.heure_feriee || 0,
                  heure_absence_non_justifiee: p.heure_absence_non_justifiee || 0,
                  heure_absence_non_justifiee_without_sanction: p.heure_absence_non_justifiee_without_sanction || 0,
                  heure_absence_maladie: p.heure_absence_maladie || 0,
                });
              }
            },
          },
          {
            key: 'primes',
            label: 'Primes',
            desc: 'Primes, banque et paiement',
            icon: DollarSign,
            tone: 'indigo',
            disabled: !paieGeneree,
            disabledHint: indispoHint,
            onClick: async () => {
              try {
                const infopaieRes = await http.get(`/api/personnel-infopaie/${p.idpersonnel}`);
                setInfopaieData(infopaieRes.data || {});
              } catch (error) {
                console.error('Erreur récupération infopaie:', error);
                setInfopaieData({});
              }
              if (isInterimMode) {
                // Mode intérim : utiliser les champs spécifiques
                ouvrir('primes', {
                  ...identite,
                  prime_eloignement: p.prime_eloignement || 0,
                  prime_de_deplacement: p.prime_de_deplacement || 0,
                  prime_astreinte: p.prime_astreinte || 0,
                  prime_de_perdiem: p.prime_de_perdiem || 0,
                  prime_autre: p.prime_autre || 0,
                });
              } else {
                // Mode normal : utiliser les champs standards
                ouvrir('primes', {
                  ...identite,
                  prime_anciennete: p.prime_anciennete || 0,
                  prime_astreinte: p.prime_astreinte || 0,
                  prime_caisse: p.prime_caisse || 0,
                  prime_responsabilite: p.prime_responsabilite || 0,
                  prime_de_deplacement: p.prime_de_deplacement || 0,
                  prime_autre: p.prime_autre || 0,
                });
              }
            },
          },
          {
            key: 'gratification',
            label: 'Gratification',
            desc: 'Ajouter ou modifier le montant',
            icon: Star,
            tone: 'amber',
            disabled: !paieGeneree,
            disabledHint: indispoHint,
            onClick: () => ouvrir('gratification', { ...identite, montant_gratification: p.prime_gratification || 0 }),
          },
          {
            key: 'regularisation',
            label: 'Régularisation',
            desc: 'Trop perçu / moins perçu',
            icon: Scale,
            tone: 'teal',
            disabled: !paieGeneree,
            disabledHint: indispoHint,
            onClick: () =>
              ouvrir('regularisation', {
                ...identite,
                montant_salaire_percu: 0,
                type_regularisation: '0',
                montant: 0,
                commentaire: '',
              }),
          },
        ],
      },
      {
        title: 'Congés',
        items: [
          {
            key: 'conges-annuels',
            label: 'Congés annuels',
            desc: 'Jours pris et moyenne mensuelle',
            icon: CalendarDays,
            tone: 'blue',
            onClick: () =>
              ouvrir('congesAnnuels', {
                ...identite,
                nombre_jour_pris_conge_annuel: p.nombre_jour_pris_conge_annuel || 0,
                moyenne_mensuelle: p.moyenne_mensuelle || 0,
              }),
          },
          {
            key: 'conges-maternite',
            label: 'Congés maternité',
            desc: 'Durée et taux applicable',
            icon: Baby,
            tone: 'pink',
            hidden: p.sexe !== 'F',
            onClick: () =>
              ouvrir('congesMaternite', {
                ...identite,
                heure_conge_matrenite: p.heure_conge_matrenite || 0,
                taux_horaire: p.taux_horaire || 0,
              }),
          },
          {
            key: 'conges-speciaux',
            label: 'Congés spéciaux',
            desc: 'Jours pris et taux applicable',
            icon: CalendarCheck,
            tone: 'purple',
            onClick: () =>
              ouvrir('congesSpeciaux', {
                ...identite,
                heure_pris_conge_speciaux: p.heure_pris_conge_speciaux || 0,
                taux_horaire: p.taux_horaire || 0,
              }),
          },
        ],
      },
      {
        title: 'Zone sensible',
        items: [
          {
            key: 'supprimer',
            label: 'Retirer de la paie',
            desc: 'Supprime cet agent du calcul en cours',
            icon: Trash2,
            tone: 'red',
            danger: true,
            onClick: async () => {
              if (window.confirm('Êtes-vous sûr de vouloir supprimer cet employé de la paie ?')) {
                try {
                  const paieId = isInterimMode ? paieEnCours?.idpaie_interime : paieEnCours?.idpaie;
                  await http.delete(`/api/paie-element/${p.idpersonnel}/${paieId}?isInterim=${isInterimMode}`);
                  fermer();
                  loadData();
                } catch (error) {
                  console.error('Erreur suppression:', error);
                  alert('Erreur lors de la suppression');
                }
              }
            },
          },
        ],
      },
    ];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PaieStyles />
      <BlueHeader
        title={
          <span>
            Calcul de Paie : <span className="text-blue-600 font-bold">{typePersonnel?.libelle || 'Chargement...'}</span>
          </span>
        }
        subtitle={paieEnCours?.libelle_paie || 'Pas de période ouverte'}
        backTo="/types-personnel-paie"
        actions={[
          {
            label: 'Générer la paie',
            icon: Play,
            onClick: handleGenererPaie,
            primary: true,
            disabled: !paieGeneree || personnel.length === 0
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {!paieEnCours && (
          <div className="paie-fade-up mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-4">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <p className="text-amber-800">Merci d'ouvrir une nouvelle période de paie pour calculer la paie.</p>
          </div>
        )}

        {/* Indicateurs clés */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={Users} label="Agents" value={personnel.length} gradient="from-blue-500 to-indigo-600" delay={0} />
          <StatCard
            icon={UserCheck}
            label="Agents intégrés"
            value={nombreIntegres}
            suffix={`/ ${personnel.length}`}
            progress={tauxIntegration}
            gradient="from-emerald-500 to-teal-600"
            delay={60}
          />
          <StatCard icon={UserX} label="Non intégrés" value={nombreNonIntegre} gradient="from-amber-500 to-orange-600" delay={120} />
          <StatCard
            icon={Wallet}
            label="Masse salariale nette"
            value={isConsolidated ? null : masseNette}
            suffix={isConsolidated ? '' : deviseAffichee}
            hint={isConsolidated ? 'Vue consolidée : devises multiples' : undefined}
            gradient="from-fuchsia-500 to-purple-600"
            delay={180}
          />
        </div>

        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`${BTN} bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50`}
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(`/personnel-non-integre/${typeId}`)}
              className={`${BTN} bg-gradient-to-r from-amber-400 to-yellow-500 text-white`}
            >
              Agents non intégrés ({nombreNonIntegre})
            </button>
            <button
              onClick={async () => {
                if (window.confirm('Êtes-vous sûr de vouloir réinitialiser ? Cela supprimera toutes les données de paie pour ce type de personnel.')) {
                  try {
                    const paieId = isInterimMode ? paieEnCours?.idpaie_interime : paieEnCours?.idpaie;
                    await http.delete(`/api/paie-element/reinitialiser/${typeId}/${paieId}?isInterim=${isInterimMode}`);
                    loadData();
                  } catch (error) {
                    console.error('Erreur réinitialisation:', error);
                    alert('Erreur lors de la réinitialisation');
                  }
                }
              }}
              className={`${BTN} bg-gradient-to-r from-rose-500 to-red-600 text-white`}
            >
              <RotateCcw className="h-4 w-4" /> Réinitialiser
            </button>
          </div>
        </div>

        {/* Onglets + recherche + tableau */}
        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="flex flex-col gap-3 border-b border-gray-100 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="lg:w-[560px]">
              <SlidingTabs
                tabs={LISTE_TABS}
                active={listTab}
                onChange={setListTab}
                label="Vues du tableau de paie"
              />
            </div>
            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, prénom ou matricule..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-10 pr-4 text-sm transition-all focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="sticky left-0 z-[1] bg-gray-50 px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Nom & Prénom</th>
                  {colonnesListe.map((c) => (
                    <th key={c.label} className="whitespace-nowrap px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">{c.label}</th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody key={listTab} className="divide-y divide-gray-100">
                {paginatedPersonnel.length === 0 ? (
                  <tr>
                    <td colSpan={colonnesListe.length + 2} className="px-6 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                        <Users className="h-6 w-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500">Aucun employé trouvé</p>
                    </td>
                  </tr>
                ) : (
                  paginatedPersonnel.map((p, i) => {
                    const integre = personnelIntegres.has(p.idpersonnel);
                    return (
                      <tr
                        key={p.idpersonnel}
                        className="paie-fade group transition-colors hover:bg-blue-50/40"
                        style={{ animationDelay: `${Math.min(i, 14) * 22}ms` }}
                      >
                        <td className="sticky left-0 z-[1] bg-white px-5 py-3 transition-colors group-hover:bg-blue-50">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-semibold text-white shadow-sm">
                              {initiales(p.nom, p.prenom)}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="truncate font-medium text-gray-900">{p.nom} {p.prenom}</span>
                                {integre ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Intégré
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gray-400" /> Non intégré
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-xs text-gray-600">
                                {p.contrat_nom && (
                                  <span className="mr-2 inline-block rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                                    {p.contrat_nom}
                                  </span>
                                )}
                                <span className="font-semibold text-gray-700">
                                  {p.titre_salaire_base || p.salaire_base || 0} {currentFiliale?.devise || 'FCFA'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        {colonnesListe.map((c) => {
                          const v = c.get(p);
                          const positif = toNumber(v) > 0;
                          const texte = c.money ? formatMontant(v) : v;
                          return (
                            <td key={c.label} className="whitespace-nowrap px-3 py-3 text-center text-sm">
                              {!positif ? (
                                <span className="text-gray-300">-</span>
                              ) : c.net ? (
                                <span className="font-bold tabular-nums text-rouge">{texte}</span>
                              ) : (
                                <span className={`inline-block rounded-lg px-2.5 py-1 font-medium tabular-nums ${TONES[c.tone]} ${c.strong ? 'font-bold' : ''}`}>{texte}</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-center">
                          <div className="relative inline-block">
                            <button
                              data-action-trigger
                              aria-haspopup="menu"
                              aria-expanded={dropdownOpen === p.idpersonnel && menuVisible}
                              onClick={() => toggleDropdown(p.idpersonnel)}
                              className={`flex items-center gap-1.5 rounded-xl border px-3.5 py-1.5 text-sm font-medium shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white disabled:hover:text-gray-700 ${
                                dropdownOpen === p.idpersonnel && menuVisible
                                  ? 'border-blue-500 bg-blue-600 text-white shadow-md'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                              disabled={!personnelIntegres.has(p.idpersonnel)}
                            >
                              Action <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropdownOpen === p.idpersonnel && menuVisible ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredPersonnel.length > 0 && (
            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3 sm:flex-row">
              <div className="text-sm text-gray-600">
                Affichage de {startIndex + 1} à {Math.min(endIndex, filteredPersonnel.length)} sur {filteredPersonnel.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Panneau d'actions latéral (portail : au-dessus de tout, jamais coupé) */}
        {dropdownOpen !== null &&
          (() => {
            const agent = personnel.find((p) => p.idpersonnel === dropdownOpen);
            if (!agent) return null;
            return (
              <ActionDrawer
                key={dropdownOpen}
                open={menuVisible}
                agent={agent}
                sections={buildActionSections(agent)}
                devise={deviseAffichee}
                onClose={fermerMenu}
              />
            );
          })()}

        {/* Modal Gratification */}
        {modalOpen === 'gratification' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Gratification : {modalData.nom} {modalData.prenom}</h3>
                <button
                  onClick={() => setModalOpen(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>NB:</strong> Les gratifications ajoutées seront dans la liste des gratifications au niveau du menu <strong>Paie - Gratifications</strong>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant de la gratification
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.montant_gratification}
                    onChange={(e) => setModalData({ ...modalData, montant_gratification: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t">
                <button
                  onClick={() => setModalOpen(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;
                      const endpoint = isInterimMode 
                        ? `/api/paie-element/paie-element-interim/${modalData.personnelId}/${paieId}`
                        : `/api/paie-element/${modalData.personnelId}/${paieId}`;
                      
                      await http.put(endpoint, {
                        prime_gratification: modalData.montant_gratification
                      });
                      setModalOpen(null);
                      loadData();
                      alert('Gratification mise à jour avec succès');
                    } catch (error) {
                      console.error('Erreur mise à jour:', error);
                      alert('Erreur lors de la mise à jour');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Congés de maternité */}
        {modalOpen === 'congesMaternite' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Congés de maternité : {modalData.nom} {modalData.prenom}</h3>
                <button
                  onClick={() => setModalOpen(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de jour Conge Maternité
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.heure_conge_matrenite}
                    onChange={(e) => setModalData({ ...modalData, heure_conge_matrenite: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Taux
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.taux_horaire}
                    onChange={(e) => setModalData({ ...modalData, taux_horaire: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t">
                <button
                  onClick={() => setModalOpen(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;
                      const endpoint = isInterimMode 
                        ? `/api/paie-element/paie-element-interim/${modalData.personnelId}/${paieId}`
                        : `/api/paie-element/${modalData.personnelId}/${paieId}`;
                      
                      await http.put(endpoint, {
                        heure_conge_matrenite: modalData.heure_conge_matrenite,
                        taux_horaire: modalData.taux_horaire
                      });
                      setModalOpen(null);
                      loadData();
                      alert('Congés de maternité mis à jour avec succès');
                    } catch (error) {
                      console.error('Erreur mise à jour:', error);
                      alert('Erreur lors de la mise à jour');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Congés spéciaux */}
        {modalOpen === 'congesSpeciaux' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Congés spéciaux : {modalData.nom} {modalData.prenom}</h3>
                <button
                  onClick={() => setModalOpen(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de jour pris
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.heure_pris_conge_speciaux}
                    onChange={(e) => setModalData({ ...modalData, heure_pris_conge_speciaux: e.target.value })}
                  />
                </div>
                {!isInterimMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taux
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={modalData.taux_horaire}
                      onChange={(e) => setModalData({ ...modalData, taux_horaire: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 p-4 border-t">
                <button
                  onClick={() => setModalOpen(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;
                      const endpoint = isInterimMode 
                        ? `/api/paie-element/paie-element-interim/${modalData.personnelId}/${paieId}`
                        : `/api/paie-element/${modalData.personnelId}/${paieId}`;
                      
                      const payload = {
                        heure_pris_conge_speciaux: modalData.heure_pris_conge_speciaux
                      };
                      
                      if (!isInterimMode) {
                        payload.taux_horaire = modalData.taux_horaire;
                      }
                      
                      await http.put(endpoint, payload);
                      setModalOpen(null);
                      loadData();
                      alert('Congés spéciaux mis à jour avec succès');
                    } catch (error) {
                      console.error('Erreur mise à jour:', error);
                      alert('Erreur lors de la mise à jour');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Congés annuels */}
        {modalOpen === 'congesAnnuels' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Congés annuels : {modalData.nom} {modalData.prenom}</h3>
                <button
                  onClick={() => setModalOpen(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de jour pris
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.nombre_jour_pris_conge_annuel}
                    onChange={(e) => setModalData({ ...modalData, nombre_jour_pris_conge_annuel: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Moyenne mensuelle
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.moyenne_mensuelle}
                    onChange={(e) => setModalData({ ...modalData, moyenne_mensuelle: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t">
                <button
                  onClick={() => setModalOpen(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;
                      const endpoint = isInterimMode 
                        ? `/api/paie-element/paie-element-interim/${modalData.personnelId}/${paieId}`
                        : `/api/paie-element/${modalData.personnelId}/${paieId}`;
                      
                      await http.put(endpoint, {
                        nombre_jour_pris_conge_annuel: modalData.nombre_jour_pris_conge_annuel,
                        moyenne_mensuelle: modalData.moyenne_mensuelle
                      });
                      setModalOpen(null);
                      loadData();
                      alert('Congés annuels mis à jour avec succès');
                    } catch (error) {
                      console.error('Erreur mise à jour:', error);
                      alert('Erreur lors de la mise à jour');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Régularisation */}
        {modalOpen === 'regularisation' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Régularisation : {modalData.nom} {modalData.prenom}</h3>
                <button
                  onClick={() => setModalOpen(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>NB:</strong> Les régularisations ajoutées seront dans la liste des régularisations au niveau du menu <strong>Paie - Régularisation</strong>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salaire perçu mois dernier
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.montant_salaire_percu}
                    onChange={(e) => setModalData({ ...modalData, montant_salaire_percu: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de régularisation
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.type_regularisation}
                    onChange={(e) => setModalData({ ...modalData, type_regularisation: e.target.value })}
                  >
                    <option value="0">Trop perçu</option>
                    <option value="1">Moins perçu</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={modalData.montant}
                    onChange={(e) => setModalData({ ...modalData, montant: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commentaire
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    value={modalData.commentaire}
                    onChange={(e) => setModalData({ ...modalData, commentaire: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t">
                <button
                  onClick={() => setModalOpen(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;
                      const paieElementTable = isInterimMode ? 'paie_element_interim' : 'paie_element';
                      
                      await http.post('/api/regularisations', {
                        personnel_id: modalData.personnelId,
                        paie_id: paieId,
                        paie_element_table: paieElementTable,
                        montant_salaire_percu: modalData.montant_salaire_percu,
                        type: modalData.type_regularisation,
                        montant: modalData.montant,
                        commentaire: modalData.commentaire
                      });
                      setModalOpen(null);
                      loadData();
                      alert('Régularisation ajoutée avec succès');
                    } catch (error) {
                      console.error('Erreur création régularisation:', error);
                      alert('Erreur lors de la création de la régularisation');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Primes */}
        {modalOpen === 'primes' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
                <h3 className="text-lg font-semibold">Primes : {modalData.nom} {modalData.prenom}</h3>
                <button
                  onClick={() => setModalOpen(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 border-b pb-2">
                  {isInterimMode ? 'Primes intérimaires' : 'Primes de paie'}
                </h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {isInterimMode ? (
                    // Mode intérim : afficher les champs spécifiques
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime d'éloignement
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_eloignement}
                          onChange={(e) => setModalData({ ...modalData, prime_eloignement: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime de déplacement
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_de_deplacement}
                          onChange={(e) => setModalData({ ...modalData, prime_de_deplacement: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime d'astreinte *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_astreinte}
                          onChange={(e) => setModalData({ ...modalData, prime_astreinte: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime de perdiem *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_de_perdiem}
                          onChange={(e) => setModalData({ ...modalData, prime_de_perdiem: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Autre prime (prime constante récursive)*
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_autre}
                          onChange={(e) => setModalData({ ...modalData, prime_autre: e.target.value })}
                        />
                      </div>
                    </>
                  ) : (
                    // Mode normal : afficher les champs standards
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime d'ancienneté
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_anciennete}
                          onChange={(e) => setModalData({ ...modalData, prime_anciennete: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime d'astreinte
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_astreinte}
                          onChange={(e) => setModalData({ ...modalData, prime_astreinte: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime de caisse
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_caisse}
                          onChange={(e) => setModalData({ ...modalData, prime_caisse: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime de responsabilité
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_responsabilite}
                          onChange={(e) => setModalData({ ...modalData, prime_responsabilite: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prime de déplacement
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_de_deplacement}
                          onChange={(e) => setModalData({ ...modalData, prime_de_deplacement: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Autre prime
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={modalData.prime_autre}
                          onChange={(e) => setModalData({ ...modalData, prime_autre: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>

                <h4 className="text-sm font-semibold text-gray-800 mb-4 border-b pb-2">Informations bancaires</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Banque
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={modalData.banque || infopaieData.banque || ''}
                      onChange={(e) => setModalData({ ...modalData, banque: e.target.value })}
                    >
                      <option value="">Sélectionner une banque</option>
                      {banques.map(b => (
                        <option key={b.idbanque} value={b.idbanque}>{b.libelle_banque}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Numéro de compte
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={modalData.numero_compte || infopaieData.numero_compte || ''}
                      onChange={(e) => setModalData({ ...modalData, numero_compte: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de paiement
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={modalData.type_paiement || infopaieData.type_paiement || ''}
                      onChange={(e) => setModalData({ ...modalData, type_paiement: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t sticky bottom-0 bg-white">
                <button
                  onClick={() => setModalOpen(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;

                      // Sauvegarder dans personnel_infopaie
                      const infopaiePayload = {
                        type_paiement: modalData.type_paiement || infopaieData.type_paiement || '',
                        banque: modalData.banque || infopaieData.banque || '',
                        numero_compte: modalData.numero_compte || infopaieData.numero_compte || '',
                      };

                      if (isInterimMode) {
                        infopaiePayload.prime_deplacement = modalData.prime_de_deplacement || 0;
                        infopaiePayload.prime_astreinte = modalData.prime_astreinte || 0;
                        infopaiePayload.prime_de_perdiem = modalData.prime_de_perdiem || 0;
                        infopaiePayload.prime_eloignement = modalData.prime_eloignement || 0;
                      } else {
                        infopaiePayload.prime_deplacement = modalData.prime_de_deplacement || 0;
                        infopaiePayload.prime_astreinte = modalData.prime_astreinte || 0;
                        infopaiePayload.prime_de_perdiem = modalData.prime_de_perdiem || 0;
                        infopaiePayload.prime_caisse = modalData.prime_caisse || 0;
                        infopaiePayload.prime_responsabilite = modalData.prime_responsabilite || 0;
                        infopaiePayload.prime_anciennete = modalData.prime_anciennete || 0;
                      }

                      await http.post(`/api/personnel-infopaie/${modalData.personnelId}`, infopaiePayload);
                      
                      // Mettre à jour dans la table appropriée
                      if (isInterimMode) {
                        // Mode intérim : mettre à jour dans paie_element_interim
                        await http.put(`/api/paie-element/paie-element-interim/${modalData.personnelId}/${paieId}`, {
                          prime_eloignement: modalData.prime_eloignement || 0,
                          prime_de_deplacement: modalData.prime_de_deplacement || 0,
                          prime_astreinte: modalData.prime_astreinte || 0,
                          prime_de_perdiem: modalData.prime_de_perdiem || 0,
                          prime_autre: modalData.prime_autre || 0,
                        });
                      } else {
                        // Mode normal : mettre à jour dans paie_element
                        await http.put(`/api/paie-element/${modalData.personnelId}/${paieId}`, {
                          prime_anciennete: modalData.prime_anciennete,
                          prime_astreinte: modalData.prime_astreinte,
                          prime_caisse: modalData.prime_caisse,
                          prime_responsabilite: modalData.prime_responsabilite,
                          prime_de_deplacement: modalData.prime_de_deplacement,
                          prime_autre: modalData.prime_autre,
                          prime_de_perdiem: modalData.prime_de_perdiem
                        });
                      }
                      setModalOpen(null);
                      loadData();
                    } catch (error) {
                      console.error('Erreur mise à jour:', error);
                      alert('Erreur lors de la mise à jour');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Récapitulatif */}
        {modalOpen === 'recap' && (
          <RecapModal
            data={modalData}
            filialeCode={recapFilialeCode}
            devise={deviseAffichee}
            onClose={() => setModalOpen(null)}
            onVoirIndicateurs={handleVoirIndicateurs}
            onVoirPrimes={() => setPrimesModalOpen(true)}
          />
        )}

        {/* Modal Infos Heures */}
        {modalOpen === 'infosHeures' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">
                  {isInterimMode ? 'Jours de travail de' : 'Heures de'} : {modalData.nom} {modalData.prenom}
                </h3>
                <button
                  onClick={() => setModalOpen(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                {isInterimMode ? (
                  // Mode intérim : afficher les champs de jours
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Présence courante *
                      </label>
                      <input
                        type="number"
                        step="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={modalData.nbreJrs_paie_courante}
                        onChange={(e) => setModalData({ ...modalData, nbreJrs_paie_courante: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Présence le mois passé *
                      </label>
                      <input
                        type="number"
                        step="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={modalData.nbreJrs_paie_passee}
                        onChange={(e) => setModalData({ ...modalData, nbreJrs_paie_passee: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Présence le mois à venir *
                      </label>
                      <input
                        type="number"
                        step="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={modalData.nbreJrs_paie_suivante}
                        onChange={(e) => setModalData({ ...modalData, nbreJrs_paie_suivante: e.target.value })}
                      />
                    </div>
                  </>
                ) : (
                  // Mode normal : afficher les champs d'heures
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Heures de présence
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={modalData.heure_presence}
                        onChange={(e) => setModalData({ ...modalData, heure_presence: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Heures fériées
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={modalData.heure_feriee}
                        onChange={(e) => setModalData({ ...modalData, heure_feriee: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Heures d'absence non justifiées
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        value={modalData.heure_absence_non_justifiee}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Heures d'absence pour sanction
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        value={modalData.heure_absence_non_justifiee_without_sanction}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Heures Permission / Absence justifiée
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        value={modalData.heure_absence_maladie}
                        disabled
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2 p-4 border-t">
                <button
                  onClick={() => setModalOpen(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const paieId = isInterimMode ? paieEnCours.idpaie_interime : paieEnCours.idpaie;
                      const payload = {
                        personnel_id: modalData.personnelId,
                        paie_id: paieId,
                      };

                      if (isInterimMode) {
                        // Mode intérim : mettre à jour les champs de jours
                        payload.nbreJrs_paie_courante = modalData.nbreJrs_paie_courante;
                        payload.nbreJrs_paie_passee = modalData.nbreJrs_paie_passee;
                        payload.nbreJrs_paie_suivante = modalData.nbreJrs_paie_suivante;
                        await http.put(`/api/paie-element/paie-element-interim/${modalData.personnelId}/${paieId}`, payload);
                      } else {
                        // Mode normal : mettre à jour les champs d'heures
                        payload.heure_presence = modalData.heure_presence;
                        payload.heure_feriee = modalData.heure_feriee;
                        await http.put(`/api/paie-element/${modalData.personnelId}/${paieId}`, payload);
                      }

                      setModalOpen(null);
                      loadData();
                      alert('Informations mises à jour avec succès');
                    } catch (error) {
                      console.error('Erreur mise à jour:', error);
                      alert('Erreur lors de la mise à jour');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal des détails des primes */}
      {primesModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-nuit">Détail des Primes</h3>
              <button onClick={() => setPrimesModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Prime Variable:</span>
                <span className="font-semibold text-blue-600">{parseFloat(modalData.prime_variable_sans_absence || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime Impactée:</span>
                <span className="font-semibold text-orange-600">{parseFloat(modalData.prime_variable || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime d'Ancienneté:</span>
                <span className="font-semibold text-purple-600">{parseFloat(modalData.prime_anciennete || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime de Caisse:</span>
                <span className="font-semibold text-green-600">{parseFloat(modalData.prime_caisse || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime de Responsabilité:</span>
                <span className="font-semibold text-teal-600">{parseFloat(modalData.prime_responsabilite || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime d'Astreinte:</span>
                <span className="font-semibold text-pink-600">{parseFloat(modalData.prime_astreinte || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime de Déplacement:</span>
                <span className="font-semibold text-indigo-600">{parseFloat(modalData.prime_de_deplacement_net || modalData.prime_de_deplacement || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime Autre:</span>
                <span className="font-semibold text-cyan-600">{parseFloat(modalData.prime_autre || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prime de Gratification:</span>
                <span className="font-semibold text-amber-600">{parseFloat(modalData.prime_gratification || 0).toLocaleString('fr-FR')}</span>
              </div>
              <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                <span className="text-gray-800">Total:</span>
                <span className="text-emerald-600">{((parseFloat(modalData.prime_variable || 0) + parseFloat(modalData.prime_anciennete || 0) + parseFloat(modalData.prime_caisse || 0) + parseFloat(modalData.prime_responsabilite || 0) + parseFloat(modalData.prime_astreinte || 0) + parseFloat(modalData.prime_de_deplacement || 0) + parseFloat(modalData.prime_autre || 0) + parseFloat(modalData.prime_gratification || 0))).toLocaleString('fr-FR')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal des détails des indicateurs */}
      {indicateursModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-nuit">Détail des Indicateurs</h3>
              <button onClick={() => setIndicateursModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-2">
              {/* Notes pondérées */}
              <div className="flex justify-between border-b border-gray-200 pb-2 mb-2">
                <span className="text-gray-700 font-semibold">Note Pondérée Quantitative:</span>
                <span className="font-semibold">{modalData.note_pondere_quantitative || 0}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-2 mb-2">
                <span className="text-gray-700 font-semibold">Note Pondérée Qualitative:</span>
                <span className="font-semibold">{modalData.note_pondere_qualitative || 0}</span>
              </div>

              {/* Autres indicateurs */}
              {indicateursData.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Aucun autre indicateur trouvé</p>
              ) : (
                indicateursData.map((ind) => (
                  <div key={ind.idperf_agent} className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-700">{ind.Libelle_indicateur || `Indicateur ${ind.idindicateur}`}</span>
                    <span className="font-semibold">{ind.valeur || 0}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculPaie;